import { Navigate, Outlet, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { WRITE_ROLES } from '@guabi/shared';
import { EmptyState } from '@/components/ui/EmptyState';
import { useMe } from './queries';
import { TopBar } from './TopBar';
import { BottomNav } from './BottomNav';
import { useCanWrite } from './useCanWrite';
import { TabBar } from './TabBar';
import { AskLauncher } from '@/features/ask/AskLauncher';

/**
 * The wedding workspace: top bar + tab row + the active tab. Every tab renders
 * inside this shell, so switching tabs never reloads the wedding context.
 */
export function AppShell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { wedId } = useParams();
  const me = useMe();
  // Above every early return: hooks cannot be called conditionally, and this
  // component returns early three times before the wedding is even resolved.
  const canWrite = useCanWrite(
    (me.data?.weddings ?? []).find((w) => w.wedId === wedId)?.wsId ?? me.data?.weddings[0]?.wsId,
  );

  if (me.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-body text-leaf-soft">
        {t('common.loading')}
      </div>
    );
  }

  if (me.isError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3">
        <p className="text-body">{t('common.somethingWrong')}</p>
        <button
          type="button"
          onClick={() => void me.refetch()}
          className="min-h-tap text-body text-brass-text underline underline-offset-4"
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  const weddings = me.data?.weddings ?? [];
  const memberships = me.data?.memberships ?? [];

  if (weddings.length === 0) {
    // Someone invited into a workspace whose wedding does not exist yet, with a
    // role that cannot create one, must not be pushed at onboarding only to be
    // refused by the API.
    const canCreate =
      memberships.length === 0 || memberships.some((m) => WRITE_ROLES.includes(m.role));
    if (!canCreate) {
      return (
        <div className="mx-auto w-full max-w-lg px-4 py-16">
          <EmptyState title={t('shell.nothingYetTitle')} body={t('shell.nothingYetBody')} />
        </div>
      );
    }
    return <Navigate to="/app/new" replace />;
  }

  const current = weddings.find((w) => w.wedId === wedId) ?? weddings[0];
  if (!current) return <Navigate to="/app/new" replace />;
  if (current.wedId !== wedId) return <Navigate to={`/app/w/${current.wedId}/home`} replace />;

  return (
    <div className="flex min-h-screen flex-col bg-paper">
      <TopBar
        wedding={current}
        weddings={weddings}
        onSwitch={(next) => navigate(`/app/w/${next}/home`)}
      />
      <TabBar wedId={current.wedId} />
      {/* The bottom bar is fixed, so the page has to end above it or the last
          row of every list sits underneath and cannot be reached. */}
      <main className="gutter mx-auto w-full max-w-[1400px] flex-1 py-6 pb-[calc(5.5rem+env(safe-area-inset-bottom))] sm:pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        {/* Said once, at the top, rather than discovered by pressing a button
            that fails. The one thing they *can* change is named, because a
            banner that only lists what you cannot do reads as an error. */}
        {!canWrite && (
          <p className="panel mb-5 p-3 text-caption text-leaf-soft" role="status">
            {t('shell.readOnly')}
          </p>
        )}
        <Outlet context={{ wedding: current, canWrite }} />
      </main>
      <BottomNav wedId={current.wedId} />
      <AskLauncher wedId={current.wedId} />
    </div>
  );
}
